import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { workRpc } from './api';
import type {
  SignoffInstructionSet,
  StationInstructionHash,
  WorkstationSignoff,
  WorkstationSignoffWithCopy,
} from './types';

/**
 * Employee matrix — workstation sign-offs (migration 133).
 *
 * The list query leaves out the `instructions` copy so the matrix stays
 * light; the record sheet loads one sign-off with its copy. Writes go
 * through `work_signoff_grant` / `work_signoff_revoke`.
 */

const SUMMARY_COLUMNS =
  'id, organization_id, workstation_id, workstation_name, user_id, signed_off_on, signed_off_by, notes, instructions_hash, revoked_at, revoked_by, revoke_reason, superseded_by, created_at, updated_at';

export const signoffKeys = {
  all: ['work', 'signoffs'] as const,
  list: () => ['work', 'signoffs', 'list'] as const,
  detail: (id: string | null) => ['work', 'signoffs', 'detail', id] as const,
  stationInstructions: (stationId: string | null) => ['work', 'signoffs', 'station-instructions', stationId] as const,
  hashes: () => ['work', 'signoffs', 'hashes'] as const,
  signedUrls: (paths: string[]) => ['work', 'signoffs', 'signed-urls', paths] as const,
};

/** Every sign-off in the org, current and past, newest first. */
export function useWorkstationSignoffs() {
  return useQuery({
    queryKey: signoffKeys.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstation_signoffs' as never)
        .select(SUMMARY_COLUMNS)
        .order('signed_off_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as WorkstationSignoff[];
    },
  });
}

/** One sign-off with its copy of the instructions. */
export function useWorkstationSignoff(id: string | null) {
  return useQuery({
    queryKey: signoffKeys.detail(id),
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstation_signoffs' as never)
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      const row = data as unknown as WorkstationSignoffWithCopy;
      return { ...row, instructions: row.instructions ?? [] };
    },
  });
}

/** The published instructions that apply at a station right now — what a sign-off would copy. */
export function useStationInstructions(stationId: string | null) {
  return useQuery({
    queryKey: signoffKeys.stationInstructions(stationId),
    enabled: !!stationId,
    queryFn: async () =>
      (await workRpc<SignoffInstructionSet[] | null>('work_station_instructions', { p_workstation_id: stationId })) ?? [],
  });
}

/** Live instruction fingerprint per station, keyed by workstation id. */
export function useStationInstructionHashes() {
  return useQuery({
    queryKey: signoffKeys.hashes(),
    queryFn: async () => {
      const rows = await workRpc<StationInstructionHash[] | null>('work_station_instruction_hashes');
      return new Map((rows ?? []).map((r) => [r.workstation_id, r]));
    },
  });
}

export type SignoffState = 'current' | 'changed' | 'revoked' | 'replaced';

/**
 * `changed` = still active, but the station's live instructions no longer
 * match the copy (edited, reordered, a set added or unpublished, …).
 */
export function signoffState(s: WorkstationSignoff, hashes: Map<string, StationInstructionHash> | undefined): SignoffState {
  if (s.revoked_at) return s.superseded_by ? 'replaced' : 'revoked';
  const live = s.workstation_id ? hashes?.get(s.workstation_id) : undefined;
  if (live && live.instructions_hash !== s.instructions_hash) return 'changed';
  return 'current';
}

export const SIGNOFF_STATE_META: Record<SignoffState, { label: string; variant: 'success-soft' | 'warning-soft' | 'muted' }> = {
  current: { label: 'Signed off', variant: 'success-soft' },
  changed: { label: 'Instructions changed', variant: 'warning-soft' },
  revoked: { label: 'Revoked', variant: 'muted' },
  replaced: { label: 'Replaced', variant: 'muted' },
};

export const matrixKey = (stationId: string, userId: string) => `${stationId}:${userId}`;

/** Batch signed URLs for the photos in a copy (used to print it). */
export function useSignoffImageUrls(paths: string[]) {
  const key = useMemo(() => [...new Set(paths)].sort(), [paths]);
  return useQuery({
    queryKey: signoffKeys.signedUrls(key),
    enabled: key.length > 0,
    staleTime: 4 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from('work-instructions').createSignedUrls(key, 60 * 10);
      if (error) throw error;
      const map = new Map<string, string>();
      for (const row of data ?? []) {
        if (row.path && row.signedUrl) map.set(row.path, row.signedUrl);
      }
      return map;
    },
  });
}

function basename(path: string): string {
  return path.split('/').pop() || 'photo.jpg';
}

export interface GrantSignoffResult {
  signoff: WorkstationSignoffWithCopy;
  /** Photos that could not be copied; those steps point at the live photo. */
  failedPhotos: number;
}

export function useGrantSignoff() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      workstationId: string;
      userId: string;
      signedOffOn: string;
      notes?: string | null;
    }): Promise<GrantSignoffResult> => {
      const orgId = profile?.organization_id;
      if (!orgId) throw new Error('Not signed in.');
      const signoffId = crypto.randomUUID();

      // Copy the step photos first so the record keeps them even if the
      // live instructions later replace or delete a photo.
      const live =
        (await workRpc<SignoffInstructionSet[] | null>('work_station_instructions', {
          p_workstation_id: input.workstationId,
        })) ?? [];
      const paths = [...new Set(live.flatMap((set) => set.steps.map((st) => st.image_path)).filter((p): p is string => !!p))];
      const copies: Record<string, string> = {};
      let failedPhotos = 0;
      await Promise.all(
        paths.map(async (path, i) => {
          const dest = `${orgId}/signoffs/${signoffId}/${i + 1}-${basename(path)}`;
          const { error } = await supabase.storage.from('work-instructions').copy(path, dest);
          if (error) failedPhotos += 1;
          else copies[path] = dest;
        }),
      );

      try {
        const signoff = await workRpc<WorkstationSignoffWithCopy>('work_signoff_grant', {
          p_workstation_id: input.workstationId,
          p_user_id: input.userId,
          p_signed_off_on: input.signedOffOn || null,
          p_notes: input.notes?.trim() || null,
          p_signoff_id: signoffId,
          p_image_copies: copies,
        });
        return { signoff, failedPhotos };
      } catch (err) {
        const made = Object.values(copies);
        if (made.length) void supabase.storage.from('work-instructions').remove(made);
        throw err;
      }
    },
    onSuccess: (res) => {
      qc.setQueryData(signoffKeys.detail(res.signoff.id), res.signoff);
      void qc.invalidateQueries({ queryKey: signoffKeys.list() });
      void qc.invalidateQueries({ queryKey: signoffKeys.hashes() });
    },
  });
}

export function useRevokeSignoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason?: string | null }) =>
      workRpc<WorkstationSignoffWithCopy>('work_signoff_revoke', {
        p_signoff_id: input.id,
        p_reason: input.reason?.trim() || null,
      }),
    onSuccess: (row) => {
      qc.setQueryData(signoffKeys.detail(row.id), row);
      void qc.invalidateQueries({ queryKey: signoffKeys.list() });
    },
  });
}
